import React, { useState } from "react";
import { motion } from "framer-motion";
import { useCourse } from "../../../../context/CourseContext";
import { useUtilityContext } from "../../../../context/UtilityContext";
import {
  Plus,
  X,
  Calendar,
  Clock,
  BookOpen,
  AlertCircle,
} from "lucide-react";
import SaveButton from "../../../../utils/CourseSaveButton";
import { useParams } from "react-router-dom";
import {
  card,
  cardTitle,
  microLabel,
  bodySecondary,
  accentBarSection,
  dividerInner,
  emptyState,
  motionFadeUp,
  motionStaggerChild,
  iconSize,
  pageTitle,
} from "../../../../lib/design-system";
import { cn } from "../../../../lib/utils";

const getDayColor = (day) => {
  const colors = {
    Monday:
      "bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700",
    Tuesday:
      "bg-purple-50 text-purple-600 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-700",
    Wednesday:
      "bg-amber-50 text-amber-600 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-700",
    Thursday:
      "bg-rose-50 text-rose-600 border-rose-200 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-700",
    Friday:
      "bg-cyan-50 text-cyan-600 border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-400 dark:border-cyan-700",
    Saturday:
      "bg-emerald-50 text-emerald-600 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-700",
    Sunday:
      "bg-orange-50 text-orange-600 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-700",
  };
  return (
    colors[day] ||
    "bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600"
  );
};

const inputClasses =
  "w-full bg-transparent focus:outline-none text-gray-900 dark:text-white";

const dateFieldClasses =
  "flex items-center gap-3 p-3 rounded-sm border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 hover:border-accent1/30 dark:hover:border-accent1/40 transition-colors";

const CourseSchedule = () => {
  const {
    courseData,
    updateCourseDate,
    addClassDayAndTime,
    removeClassDayAndTime,
  } = useCourse();

  const [newDay, setNewDay] = useState("");
  const [newTime, setNewTime] = useState("");
  const [isAddingTime, setIsAddingTime] = useState(false);
  const { courseID: urlCourseID } = useParams();
  const { selectedCourseId } = useUtilityContext();
  const courseID = urlCourseID || selectedCourseId;

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().split("T")[0];
  };

  const handleDateChange = (dateType, value) => {
    updateCourseDate(dateType, value);
  };

  const handleAddDayAndTime = () => {
    if (newDay && newTime) {
      addClassDayAndTime(newDay, newTime);
      setNewDay("");
      setNewTime("");
      setIsAddingTime(false);
    }
  };

  const dateFields = [
    {
      label: "Class Starts From",
      key: "classStartDate",
      icon: Calendar,
    },
    {
      label: "Class Ends On",
      key: "classEndDate",
      icon: Calendar,
    },
    {
      label: "Mid-Semester Exam",
      key: "midSemesterExamDate",
      icon: BookOpen,
    },
    {
      label: "End Semester Exam",
      key: "endSemesterExamDate",
      icon: BookOpen,
    },
  ];

  const classTimes = courseData?.courseSchedule?.classDaysAndTimes || [];

  return (
    <motion.div {...motionFadeUp} className="mx-auto space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={pageTitle}>Class Schedule</h1>
          <p className={cn(bodySecondary, "mt-1")}>
            Manage important dates and class timings
          </p>
          <div className={cn(accentBarSection, "mt-3")} />
        </div>
        <SaveButton urlId={courseID} />
      </div>

      {/* Two-column grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Important Dates card */}
        <motion.div {...motionStaggerChild(0)} className={cn(card, "overflow-hidden")}>
          <div
            className={cn(
              "px-4 sm:px-5 py-4 flex items-center gap-3 border-b",
              dividerInner
            )}
          >
            <h2 className={cardTitle}>Important Dates</h2>
          </div>

          <div className="px-4 sm:px-5 py-4 space-y-4">
            {dateFields.map((field) => {
              const Icon = field.icon;
              return (
                <div key={field.key}>
                  <label className={cn(microLabel, "mb-1.5 block")}>
                    {field.label}
                  </label>
                  <div className={dateFieldClasses}>
                    <Icon
                      className={cn(iconSize.md, "text-accent1 flex-shrink-0")}
                    />
                    <input
                      type="date"
                      value={formatDateForInput(
                        courseData.courseSchedule[field.key]
                      )}
                      onChange={(e) =>
                        handleDateChange(field.key, e.target.value)
                      }
                      className={inputClasses}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Class Days & Times card */}
        <motion.div {...motionStaggerChild(1)} className={cn(card, "overflow-hidden")}>
          <div
            className={cn(
              "px-4 sm:px-5 py-4 flex items-center gap-3 border-b",
              dividerInner
            )}
          >
            <h2 className={cardTitle}>Class Days & Times</h2>
          </div>

          <div className="px-4 sm:px-5 py-4">
            {classTimes.length > 0 ? (
              <div className="space-y-2">
                {classTimes.map((schedule, index) => (
                  <div
                    key={index}
                    className="group flex items-center justify-between p-3 rounded-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 hover:border-accent1/30 dark:hover:border-accent1/40 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={cn(
                          "px-2.5 py-1 rounded-sm text-xs font-bold border",
                          getDayColor(schedule.day)
                        )}
                      >
                        {schedule.day}
                      </span>
                      <div className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-200">
                        <Clock
                          className={cn(
                            iconSize.xs,
                            "text-gray-400 dark:text-gray-500"
                          )}
                        />
                        <span>{schedule.time}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => removeClassDayAndTime(index)}
                      className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-sm text-red-500 dark:text-red-400 transition-all"
                      aria-label="Remove class time"
                    >
                      <X className={iconSize.sm} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className={emptyState}>
                <Clock
                  className={cn(
                    iconSize.empty,
                    "text-gray-300 dark:text-gray-600 mx-auto mb-3"
                  )}
                />
                <h3 className={cn(cardTitle, "mb-1")}>No Class Times</h3>
                <p className={cn(bodySecondary, "mb-0")}>
                  Add your first class day and time below.
                </p>
              </div>
            )}

            {/* Add time form or button */}
            {isAddingTime ? (
              <div className="mt-4 p-4 rounded-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 space-y-3">
                <h3 className={cn(cardTitle, "text-sm")}>Add New Class Time</h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className={cn(microLabel, "mb-1.5 block")}>
                      Day
                    </label>
                    <select
                      value={newDay}
                      onChange={(e) => setNewDay(e.target.value)}
                      className="w-full p-2.5 rounded-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-accent1 transition-colors"
                    >
                      <option value="">Select Day</option>
                      <option value="Monday">Monday</option>
                      <option value="Tuesday">Tuesday</option>
                      <option value="Wednesday">Wednesday</option>
                      <option value="Thursday">Thursday</option>
                      <option value="Friday">Friday</option>
                      <option value="Saturday">Saturday</option>
                      <option value="Sunday">Sunday</option>
                    </select>
                  </div>

                  <div>
                    <label className={cn(microLabel, "mb-1.5 block")}>
                      Time
                    </label>
                    <input
                      type="text"
                      value={newTime}
                      onChange={(e) => setNewTime(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleAddDayAndTime();
                      }}
                      placeholder="e.g., 6 pm to 7.30 pm"
                      className="w-full p-2.5 rounded-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 text-sm focus:outline-none focus:border-accent1 transition-colors"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-1">
                  <button
                    onClick={() => {
                      setIsAddingTime(false);
                      setNewDay("");
                      setNewTime("");
                    }}
                    className="px-4 py-2 rounded-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-bold transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddDayAndTime}
                    className="px-4 py-2 bg-accent1 text-white rounded-sm text-sm font-bold hover:bg-accent1/90 transition-colors shadow-sm"
                  >
                    Add Class Time
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setIsAddingTime(true)}
                className="flex items-center justify-center w-full mt-4 p-3 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-sm text-gray-400 dark:text-gray-500 hover:border-accent1 hover:text-accent1 transition-colors text-sm"
              >
                <Plus className={cn(iconSize.md, "mr-2")} />
                Add New Day and Time
              </button>
            )}
          </div>
        </motion.div>
      </div>

      {/* Info tip panel */}
      <div className="flex items-start gap-4 p-5 rounded-sm bg-accent1/5 dark:bg-accent1/10 border border-accent1/20 dark:border-accent1/30">
        <AlertCircle
          className={cn(iconSize.lg, "text-accent1 mt-0.5 flex-shrink-0")}
        />
        <div>
          <p className="text-sm font-bold text-accent1 mb-1">
            Scheduling Tips
          </p>
          <p className={bodySecondary}>
            Make sure to keep your class schedule up to date. Students will be
            notified automatically of any changes to these dates.
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default CourseSchedule;
